'use client';

import { SystemHealthBanner } from '@order-up/common';
import { useAppCheckHealth } from '../hooks/useAppCheckHealth';

export interface AppCheckHealthBannerProps {
  /** Context-specific heading. Defaults to a surface-neutral message. */
  title?: string;
  /** Context-specific body. Defaults to a surface-neutral message. */
  message?: string;
}

/**
 * Proactive "it's us" notice for a degraded Firebase App Check attestation.
 *
 * When App Check health is `degraded` (token attestation is failing, e.g. a
 * reCAPTCHA Enterprise domain/config problem), the symptom users hit is an
 * opaque failure that reads like *their* mistake. This banner watches the live
 * App Check health signal and, only while degraded, renders a plain-English
 * service notice so the user knows the problem is on our side.
 *
 * Shared across surfaces (sign-in AND in-app shells): copy is passed by prop so
 * the auth app can say "Sign-in may be unavailable" while the management shell
 * shows surface-neutral copy — the degraded-only render logic stays shared.
 * Renders nothing in `unknown`/`healthy`; the hook is reactive so it appears/
 * disappears in lockstep with the signal.
 */
export function AppCheckHealthBanner({
  title,
  message,
}: AppCheckHealthBannerProps = {}) {
  const health = useAppCheckHealth();

  if (health !== 'degraded' && health !== 'refused') {
    return null;
  }

  return (
    <SystemHealthBanner
      status="partial_outage"
      title={title ?? 'We’re having trouble verifying your browser'}
      message={
        message ??
        (health === 'refused'
          // Refused is final for this page load; a reload runs a fresh check.
          ? 'This is on our side, not your account. Please reload the page.'
          : 'This is on our side, not your account. Please try again in a few minutes.')
      }
    />
  );
}
